import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import { faHeartHalfStroke } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateSoftCreditById } from 'api/transactions';
import * as ActivityItem from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/ActivityItem';
import * as DropdownMenu from 'components/dropdown-menu';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { ContactType } from 'constants/contacts';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useForceRender } from 'customHooks/useForceRender';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { SoftCreditActivity } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface SoftCreditFormValues {
    note: string;
}

interface SoftCreditActivityProps {
    item: SoftCreditActivity;
}

const SoftCredit = ({ item }: SoftCreditActivityProps) => {
    const { accountId } = useCurrentAccount();
    const { data: currentContact } = useCurrentContact();
    const { key, forceRender } = useForceRender();
    const { success, error } = useToast();

    const softCredit = item.resource;
    const transaction = softCredit.transaction;
    const transactionContactResource =
        transaction.contact.type === ContactType.Company ? 'companies' : 'contacts';
    const currentContactResource =
        currentContact.type === ContactType.Company ? 'companies' : 'contacts';
    const contactName =
        transaction.contact.type === ContactType.Company
            ? transaction.contact.company_name
            : `${transaction.contact.first_name} ${transaction.contact.last_name}`;

    const methods = Form.useGivebutterForm<SoftCreditFormValues>({
        defaultValues: {
            note: softCredit.note ?? '',
        },
    });
    const { control, handleSubmit, path } = methods;

    const { mutate: saveNote, isPending } = useMutation({
        mutationFn: (values: SoftCreditFormValues) =>
            updateSoftCreditById(accountId, transaction.id, softCredit.id, values),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactActivities(accountId, currentContact.id),
            });
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            success('Saved activity item note!');
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <ActivityItem.Root key={key}>
            <ActivityItem.Trigger>
                <ActivityItem.Icon className="bg-blue-50">
                    <FontAwesomeIcon className="text-blue-500" icon={faHeartHalfStroke} />
                </ActivityItem.Icon>
                <p className="flex flex-col gap-1 text-left">
                    <span>
                        Soft credited with a gift to{' '}
                        <Link to={`/accounts/${accountId}/campaigns/${transaction.campaign.id}`}>
                            {transaction.campaign.title}
                        </Link>{' '}
                        from{' '}
                        <Link
                            to={`/accounts/${accountId}/${transactionContactResource}/${transaction.contact.id}`}
                        >
                            {contactName}
                        </Link>
                    </span>
                    <span className="text-sm text-gray-600">
                        {dateFromServer(softCredit.created_at).toLocaleString(DateTime.DATE_FULL)}
                    </span>
                </p>
                <div className="ml-auto flex items-center gap-5">
                    <p>
                        <CurrencySymbol />
                        {maskDecimal(softCredit.amount)}
                    </p>
                    <DropdownMenu.Root>
                        <DropdownMenu.EllipsisTrigger
                            aria-label="Actions"
                            className="w-5 text-center"
                            onClick={event => event.stopPropagation()}
                        />
                        <DropdownMenu.Content onClick={e => e.stopPropagation()}>
                            <DropdownMenu.LinkItem
                                to={`/accounts/${accountId}/${currentContactResource}/${currentContact.id}/transactions/${transaction.id}`}
                            >
                                View transaction
                            </DropdownMenu.LinkItem>
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                </div>
            </ActivityItem.Trigger>
            <ActivityItem.Content>
                <Form.GivebutterFormProvider {...methods}>
                    <form onSubmit={handleSubmit(values => saveNote(values))}>
                        <EditableInput.Root>
                            <EditableInput.ReadOnly>
                                <ActivityItem.Note>{softCredit.note}</ActivityItem.Note>
                            </EditableInput.ReadOnly>
                            <EditableInput.Content>
                                <Form.Container>
                                    <Form.Label name={path('note')}>Note</Form.Label>
                                    <Form.UseFormTextArea control={control} name={path('note')} />
                                </Form.Container>
                                <EditableInput.ActionsContainer>
                                    <EditableInput.Cancel />
                                    <Form.SubmitButton isSubmitting={isPending}>
                                        Save
                                    </Form.SubmitButton>
                                </EditableInput.ActionsContainer>
                            </EditableInput.Content>
                            <ActivityItem.Actions>
                                <EditableInput.ReadOnlyEditTrigger asChild>
                                    <Button color="neutral" variant="transparent">
                                        Edit
                                    </Button>
                                </EditableInput.ReadOnlyEditTrigger>
                            </ActivityItem.Actions>
                        </EditableInput.Root>
                    </form>
                </Form.GivebutterFormProvider>
            </ActivityItem.Content>
        </ActivityItem.Root>
    );
};

export { SoftCredit };
